// The single session-scoped `Connection` owner (design D3, client-wire "Single
// session-scoped connection ownership"). The in-match `Connection` is owned once
// per match session — a `MatchSession` shared by the colony, match, and in-match
// canvas surfaces — never constructed per screen. A screen that needs the stream
// or the mutation seam is handed the existing connection rather than opening its
// own, so a session never holds two same-token sockets that would self-evict under
// the MATCH-LIFECYCLE §1 same-token-replace rule when the player navigates between
// screens.
//
// `getOrCreateMatchSession` memoizes by session key (session token + match id): the
// first caller (whichever screen mounts first) builds the session via its supplied
// factory; every later call for the same key — including a screen re-mounting after
// navigating away and back — is handed the same `MatchSession`/`Connection`
// instance, never a second one.

import { Connection } from '../net/wire/connection';
import type { ConnectionCallbacks, ConnectionConfig, ConnectionDeps } from '../net/wire/connection';

/** Identifies one match session: a session token scoped to one match id. */
export interface MatchSessionKey {
  sessionToken: string;
  matchId: string;
}

/** Composes a stable registry key from a `MatchSessionKey`. Exported so callers can
 * key their own bookkeeping (e.g. a store field naming the active session) the same
 * way the registry does. */
export function matchSessionKey(key: MatchSessionKey): string {
  return `${key.sessionToken}::${key.matchId}`;
}

/** Everything needed to construct the one `Connection` a `MatchSession` owns. */
export interface MatchSessionConfig {
  hello: ConnectionConfig['hello'];
  config: Omit<ConnectionConfig, 'hello'>;
  deps: ConnectionDeps;
  callbacks?: ConnectionCallbacks;
}

/**
 * One session-scoped `Connection` owner. Constructed exactly once per session key
 * by `getOrCreateMatchSession`; every consumer (colony screen, match screen,
 * in-match canvas) is handed this same instance and reads `.connection` rather than
 * building its own.
 */
export class MatchSession {
  readonly connection: Connection;

  constructor(config: MatchSessionConfig) {
    this.connection = new Connection(
      { hello: config.hello, ...config.config },
      config.deps,
      config.callbacks ?? {},
    );
  }
}

/** Module-level registry: one `MatchSession` per session key, for the lifetime of
 * the page (until `clearMatchSession`/`resetAllMatchSessions` explicitly forgets
 * one) — this is exactly what makes screen navigation reuse the connection instead
 * of racing a fresh one into existence. */
const registry = new Map<string, MatchSession>();

/**
 * Returns the existing `MatchSession` for `key` if one is already registered;
 * otherwise builds one from `factory()` and registers it. `factory` is only ever
 * invoked on the first call for a given key — a screen navigating in after the
 * session already exists never re-runs it and never opens a second same-token
 * socket.
 */
export function getOrCreateMatchSession(
  key: MatchSessionKey,
  factory: () => MatchSessionConfig,
): MatchSession {
  const registryKey = matchSessionKey(key);
  const existing = registry.get(registryKey);
  if (existing !== undefined) {
    return existing;
  }
  const session = new MatchSession(factory());
  registry.set(registryKey, session);
  return session;
}

/** Returns the session already registered for `key`, or `undefined` if none has
 * been created yet — for a consumer that must never accidentally create one. */
export function getMatchSession(key: MatchSessionKey): MatchSession | undefined {
  return registry.get(matchSessionKey(key));
}

/** Disconnects (if live) and forgets the session for `key`, so a later
 * `getOrCreateMatchSession` call for the same key starts fresh (e.g. leaving a
 * match, or test isolation between cases). */
export function clearMatchSession(key: MatchSessionKey): void {
  const registryKey = matchSessionKey(key);
  const existing = registry.get(registryKey);
  if (existing !== undefined) {
    existing.connection.disconnect();
    registry.delete(registryKey);
  }
}

/** Test-only: clears every registered session. The registry is module-level state,
 * so tests that construct sessions must reset it between cases to avoid one test's
 * session leaking into the next. */
export function resetAllMatchSessions(): void {
  for (const session of registry.values()) {
    session.connection.disconnect();
  }
  registry.clear();
}
